import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { calculateServiceCost, OutSourceConfig, OutsourceCost, OutsourceProvider } from 'entities';

import { Switch, Column, Row, Label } from 'components/Blocks';
import { MonetaryInput } from 'components/Inputs';
import { InfoIcon } from 'components/Icons';

import { OutsourceProviderSelect } from 'components/Entities';

import { StepComponentProps } from '../types';

import { omitUndefined } from 'utils/object';

export function Outsourced({ state, dispatch }: StepComponentProps) {
  const { t } = useLang();

  const handleOutsourceToggle = (index: number): void => {
    dispatch({
      services: ({ services }) => {
        const isCurrentlySelected = !!services[index].outsourced;

        return services.map((service, serviceIndex) =>
          index === serviceIndex
            ? (omitUndefined({
                ...service,
                outsourced: isCurrentlySelected ? undefined : true,
                outsourceConfig: isCurrentlySelected
                  ? undefined
                  : {
                      providerName: '',
                      providerCost: {
                        currency: state.currency,
                        value: '',
                      },
                      clientCost: {
                        currency: state.currency,
                        value: calculateServiceCost(service),
                      },
                    },
              }) as (typeof state.services)[number])
            : service,
        );
      },
    });
  };

  const handleProviderChange = useCallback(
    (provider: OutsourceProvider, index: number) => {
      dispatch({
        services: ({ services }) => {
          return services.map((service, serviceIndex) =>
            index === serviceIndex
              ? ({
                  ...service,
                  outsourceConfig: {
                    ...service.outsourceConfig,
                    provider: provider.id,
                    providerName: provider.name,
                  },
                } as (typeof services)[number])
              : service,
          );
        },
      });
    },
    [dispatch],
  );

  const handleChange = useCallback(
    ({ target }: { target: { name: string; value: string } }) => {
      const { name, value } = target;

      const [index, firstLevelProp, secondLevelProp] = name.split('#');

      dispatch({
        services: ({ services }) => {
          return services.map((service, serviceIndex) =>
            Number(index) === serviceIndex
              ? ({
                  ...service,
                  outsourceConfig: secondLevelProp
                    ? {
                        ...service.outsourceConfig,
                        [firstLevelProp]: {
                          ...((service.outsourceConfig as OutSourceConfig)[
                            firstLevelProp as keyof OutSourceConfig
                          ] as OutsourceCost),
                          [secondLevelProp]: value,
                        },
                      }
                    : {
                        ...service.outsourceConfig,
                        [firstLevelProp]: value,
                      },
                } as (typeof services)[number])
              : service,
          );
        },
      });
    },
    [dispatch],
  );

  return (
    <>
      <p className="font-medium">{t('is-there-outsource')}</p>

      <Row justifyContent="center">
        <InfoIcon color="primary" size={32} />

        <p className="max-w-[90%]">{t('service-outsource-helper')}</p>
      </Row>

      {state.services.map((service, index) => (
        <Column className="rounded-md border p-4" key={`${service.id}-outsourced-row`} gap="6">
          <Row justifyContent="space-between">
            <Label>{service.name}</Label>

            <Switch
              checked={!!service.outsourced}
              onChange={() => handleOutsourceToggle(index)}
              size="small"
            />
          </Row>

          {service.outsourceConfig && (
            <Column>
              <OutsourceProviderSelect
                value={service.outsourceConfig.provider || ''}
                onChange={(provider) => handleProviderChange(provider, index)}
              />

              <Column gap="2">
                <Label>{t('provide-cost')}</Label>

                <MonetaryInput
                  onChange={handleChange}
                  currency={service.outsourceConfig.providerCost?.currency}
                  currencyDisabled
                  name={`${index}#providerCost#value`}
                  value={service.outsourceConfig.providerCost?.value}
                  required
                />
              </Column>

              <Column gap="2">
                <Label>{t('cost-to-client')}</Label>

                <MonetaryInput
                  disabled
                  currency={service.outsourceConfig.clientCost?.currency}
                  value={service.outsourceConfig.clientCost?.value}
                />
              </Column>
            </Column>
          )}
        </Column>
      ))}
    </>
  );
}
